import { Mail, Smartphone, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { RowListSkeleton } from '../../../../../shared/components/RowListSkeleton';
import { localMobile } from '../../../../../shared/policies/local-mobile';
import { accountTabPath } from '../../../account.paths';
import type { SignInCodeChannelsState } from '../hooks/use-sign-in-code-channels';
import { phoneCodeChannelsLabel } from '../policies/phone-code-channels';
import { SecurityCard } from './SecurityCard';

interface SignInCodesCardProps {
  email: string;
  phone: string | null;
  channels: SignInCodeChannelsState;
}

function CodeDestination({ icon: Icon, value, how }: { icon: LucideIcon; value: ReactNode; how: string }) {
  return (
    <li className="flex items-center gap-3 py-3">
      <Icon className="size-4 shrink-0 text-muted" />
      <div className="min-w-0">
        <p className="text-sm break-words text-fg">{value}</p>
        <p className="text-xs text-muted">{how}</p>
      </div>
    </li>
  );
}

function phoneHow(channels: SignInCodeChannelsState): string {
  if (channels.status === 'error') return "Couldn't check how codes reach your phone here";
  return channels.status === 'loaded' ? phoneCodeChannelsLabel(channels.channels) : '';
}

/** Where a sign-in code can go: the email always, the mobile by whichever phone channels this deployment has set up. */
export function SignInCodesCard({ email, phone, channels }: SignInCodesCardProps) {
  return (
    <SecurityCard title="Sign-in codes" description="You sign in with a 6-digit code. It can go to any of these.">
      {channels.status === 'loading' ? (
        <div className="mt-4">
          <RowListSkeleton label="Loading sign-in codes" rows={2} />
        </div>
      ) : (
        <ul className="mt-2 divide-y divide-line">
          <CodeDestination icon={Mail} value={email} how="By email" />
          {phone ? (
            <CodeDestination icon={Smartphone} value={localMobile(phone)} how={phoneHow(channels)} />
          ) : (
            <CodeDestination
              icon={Smartphone}
              value={
                <>
                  No mobile number — <Link to={accountTabPath('profile')}>add one on Profile</Link>
                </>
              }
              how="Codes go to your email until then"
            />
          )}
        </ul>
      )}
    </SecurityCard>
  );
}
